import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Button, Card, cx, Empty, ErrorNote, Loading, Section, SeverityBadge } from '../components/ui';
import type { Alarm } from '../lib/api';
import { fmtAgo, fmtDuration, fmtTime, humanize } from '../lib/format';

/** Alarm ids are "<asset>:<alarm>"; the same alarm often exists on several assets. */
const assetOf = (alarmId: string) => humanize(alarmId.split(':')[0] ?? '');
import { useAlarmLog, useAlarms, useAlarmSummary } from '../lib/queries';
import { usePlantContext } from './Plant';

export function ActiveAlarms({ alarms, timezone, showPlant }: { alarms: Alarm[]; timezone: (plantSlug: string) => string; showPlant: boolean }) {
  if (alarms.length === 0) return <Empty>No active alarms.</Empty>;
  return (
    <Card className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted">
          <tr>
            <th className="px-3 py-2 font-medium">Severity</th>
            {showPlant && <th className="px-3 py-2 font-medium">Plant</th>}
            <th className="px-3 py-2 font-medium">Alarm</th>
            <th className="px-3 py-2 font-medium">Since</th>
            <th className="px-3 py-2 text-right font-medium">Times</th>
          </tr>
        </thead>
        <tbody>
          {alarms.map((a) => (
            <tr key={`${a.plant.id}:${a.id}`} className="border-t border-rule">
              <td className="px-3 py-2"><SeverityBadge severity={a.severity} /></td>
              {showPlant && <td className="px-3 py-2"><Link className="hover:underline" to={`/plants/${a.plant.slug}`}>{a.plant.name}</Link></td>}
              <td className="px-3 py-2">
                <div className="font-medium"><span className="text-muted">{humanize(a.asset)} · </span>{a.name}</div>
                {a.message && <div className="text-xs text-muted">{a.message}</div>}
              </td>
              <td className="tabular px-3 py-2 whitespace-nowrap" title={a.activatedAt ? fmtTime(a.activatedAt, timezone(a.plant.slug), { seconds: true }) : undefined}>
                {a.activatedAt ? `${fmtTime(a.activatedAt, timezone(a.plant.slug))} · ${fmtAgo(a.activatedAt)}` : '–'}
              </td>
              <td className="tabular px-3 py-2 text-right">{a.activationCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

const PERIODS = [
  { key: '24h', label: '24 h', ms: 86_400_000 },
  { key: '7d', label: '7 days', ms: 7 * 86_400_000 },
  { key: '30d', label: '30 days', ms: 30 * 86_400_000 },
];

/** Active alarms, top alarms and the event log; for one plant or all you can see. */
export function AlarmViews({ plant, timezone }: { plant?: string; timezone: (plantSlug: string) => string }) {
  const [period, setPeriod] = useState(PERIODS[1]!);
  const from = useMemo(() => new Date(Date.now() - period.ms).toISOString(), [period]);
  const active = useAlarms({ plant, active: 'true' });
  const summary = useAlarmSummary({ plant, from });
  const log = useAlarmLog({ plant, from });
  const tzFor = (slug: string) => (plant ? timezone(plant) : timezone(slug));

  return (
    <>
      <Section title="Active">
        {active.isPending ? <Loading /> : active.error ? <ErrorNote error={active.error} /> : (
          <ActiveAlarms alarms={active.data} timezone={tzFor} showPlant={!plant} />
        )}
      </Section>

      <div className="mb-3 flex items-center gap-2">
        <span className="text-sm text-muted">Period</span>
        <div className="flex rounded-md border border-rule bg-surface p-0.5">
          {PERIODS.map((p) => (
            <button key={p.key} type="button" onClick={() => setPeriod(p)} className={cx('rounded px-2.5 py-1 text-sm', p.key === period.key ? 'bg-accent-soft font-medium' : 'text-muted hover:text-ink')}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <Section title="Most frequent">
        {summary.isPending ? <Loading /> : summary.error ? <ErrorNote error={summary.error} /> : summary.data.data.length === 0 ? <Empty>No alarms in this period.</Empty> : (
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Severity</th>
                  {!plant && <th className="px-3 py-2 font-medium">Plant</th>}
                  <th className="px-3 py-2 font-medium">Alarm</th>
                  <th className="px-3 py-2 text-right font-medium">Activations</th>
                  <th className="px-3 py-2 text-right font-medium">Time active</th>
                </tr>
              </thead>
              <tbody>
                {summary.data.data.map((s) => (
                  <tr key={`${s.plant.id}:${s.alarmId}`} className="border-t border-rule">
                    <td className="px-3 py-2"><SeverityBadge severity={s.severity} /></td>
                    {!plant && <td className="px-3 py-2">{s.plant.name}</td>}
                    <td className="px-3 py-2"><span className="text-muted">{assetOf(s.alarmId)} · </span>{s.name ?? s.alarmId}</td>
                    <td className="tabular px-3 py-2 text-right">{s.activations}</td>
                    <td className="tabular px-3 py-2 text-right">{fmtDuration(s.activeSeconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </Section>

      <Section title="Events">
        {log.isPending ? <Loading /> : log.error ? <ErrorNote error={log.error} /> : (
          (() => {
            const events = log.data.pages.flatMap((p) => p.data);
            if (events.length === 0) return <Empty>No alarm events in this period.</Empty>;
            return (
              <Card className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-muted">
                    <tr>
                      <th className="px-3 py-2 font-medium">Time</th>
                      {!plant && <th className="px-3 py-2 font-medium">Plant</th>}
                      <th className="px-3 py-2 font-medium">Alarm</th>
                      <th className="px-3 py-2 font-medium">Event</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={`${e.time}:${e.plant.id}:${e.alarmId}:${e.event}`} className="border-t border-rule">
                        <td className="tabular px-3 py-1.5 whitespace-nowrap">{fmtTime(e.time, tzFor(e.plant.slug), { seconds: true })}</td>
                        {!plant && <td className="px-3 py-1.5">{e.plant.name}</td>}
                        <td className="px-3 py-1.5"><span className="mr-2"><SeverityBadge severity={e.severity} /></span><span className="text-muted">{assetOf(e.alarmId)} · </span>{e.name ?? e.alarmId}</td>
                        <td className={cx('px-3 py-1.5 capitalize', e.event === 'activated' && 'font-medium')}>{e.event}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {log.hasNextPage && (
                  <div className="border-t border-rule p-2 text-center">
                    <Button variant="ghost" onClick={() => log.fetchNextPage()} disabled={log.isFetchingNextPage}>Load more</Button>
                  </div>
                )}
              </Card>
            );
          })()
        )}
      </Section>
    </>
  );
}

export function PlantAlarms() {
  const plant = usePlantContext();
  return <AlarmViews plant={plant.slug} timezone={() => plant.timezone} />;
}
